import {Component, EventEmitter, Input, Output, SimpleChanges, ViewChild} from '@angular/core';
import {NgbCalendar, NgbDate, NgbDatepicker, NgbDateStruct, NgbTimeStruct} from "@ng-bootstrap/ng-bootstrap";

@Component({
  selector: 'app-calendar',
  templateUrl: './calendar.component.html',
  styleUrl: './calendar.component.scss'
})
export class CalendarComponent {
  public static readonly PAGE_SIZE: number = 15;
  @ViewChild('calendar') calendarInstance?: NgbDatepicker;

  @Input() maxDate: NgbDate | undefined;
  @Input() minDate: NgbDate | undefined;
  @Input() openDate?: Date = new Date();
  @Input() isRange?: boolean = false;
  @Input() fromDate: NgbDate | any = null;
  @Input() toDate: NgbDate | any = null;
  @Input() showSelection: boolean = false;
  @Input() alignment: "vertical" | "horizontal" = "horizontal";
  hoveredDate: NgbDate | null = null;

  @Output() onSelectionChange: EventEmitter<Date> = new EventEmitter<Date>();
  @Output() onRangeChange: EventEmitter<{from: NgbDate | undefined, to: NgbDate | undefined}>= new EventEmitter<{from: NgbDate | undefined; to: NgbDate | undefined}>();
  @Input() selectedDate: Date | undefined;
  currentDate: Date = new Date();
  CalendarState = CalendarState;
  calendarState: CalendarState = CalendarState.DAY;
  navigationDate: NgbDate = this.getNgbDate(this.currentDate);

  months: NavigationObject[] = [];
  years: NavigationObject [] = [];


  constructor(private ngbCalendar: NgbCalendar) {
  }

  ngOnInit() {
    this.initMonths();
    this.markDisabledDates();
    this.navigationDate = this.isRange ? this.fromDate ?? this.toDate ?? this.navigationDate : this.getNgbDate(this.selectedDate ?? this.currentDate);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (
        changes['maxDate']?.currentValue !== changes['maxDate']?.previousValue ||
        changes['fromDate']?.currentValue !== changes['fromDate']?.previousValue ||
        changes['toDate']?.currentValue !== changes['toDate']?.previousValue ||
        changes['minDate']?.currentValue !== changes['minDate']?.previousValue
    ) {
      this.markDisabledDates();
    }
    if (
        changes['openDate']?.currentValue !== changes['openDate']?.previousValue
    ) {
      this.navigationDate = this.getNgbDate(this.openDate)
    }
  }

  changeCalendarState(calendarState: CalendarState) {
    if (this.calendarState === calendarState) {
      this.calendarState = CalendarState.DAY
    } else {
      this.calendarState = calendarState;
      if (calendarState === CalendarState.YEAR) {
        this.initYears();
      }
      if (calendarState === CalendarState.MONTH) {
        this.updateMonths();
      }
    }
  }

  updateMonths() {
    const maxDate: NgbDate = this.maxCalendarDate;
    const minDate: NgbDate = this.minCalendarDate;
    for (let month of this.months) {
      month.disabled = false;
      if (this.navigationDate.year === maxDate.year) {
        if (month.value > maxDate.month) {
          month.disabled = true;
        }
      }
      if (this.navigationDate.year === minDate.year) {
        if (month.value < minDate.month) {
          month.disabled = true;
        }
      }
    }
  }

  navigateNext() {
    switch (this.calendarState) {
      case CalendarState.DAY:
        this.navigateToNextMonth();
        break;
      case CalendarState.YEAR:
        this.navigateNextYears();
        break;
    }
  }

  navigateNextYears(): void {
    if (this.years.at(this.years.length - 1)?.value && this.years.at(this.years.length - 1)!.value < this.maxCalendarDate.year) {
      let from = this.years.at(this.years.length - 1)!.value;
      let to = from + CalendarComponent.PAGE_SIZE
      this.years = [];
      for (let i = from + 1; i <= to; i++) {
        this.years.push(new NavigationObject(i, i > this.maxCalendarDate.year));
      }
    }
  }

  navigateToNextMonth() {
    if (this.calendarInstance?.calendar) {
      let next: NgbDate = this.calendarInstance.calendar.getNext(this.navigationDate, 'm');
      if (this.minDate && next.before(this.minDate)) {
        next = this.minDate;
      }
      if (next.year < this.maxCalendarDate.year || (next.year == this.maxCalendarDate.year && next.month <= this.maxCalendarDate.month)) {
        this.navigationDate = next;
        this.calendarInstance?.navigateTo(this.navigationDate);
      }
    }
  }

  markDisabled = (date: NgbDateStruct) => false;


  navigatePrevious() {
    switch (this.calendarState) {
      case CalendarState.DAY:
        this.navigateToPreviousMonth();
        break;
      case CalendarState.YEAR:
        this.navigatePreviousYears();
        break;
    }
  }

  navigatePreviousYears(): void {
    if (this.years.at(0)?.value && this.years.at(0)!.value > this.minCalendarDate.year) {
      let to = this.years.at(0)!.value;
      let from = to - CalendarComponent.PAGE_SIZE
      this.years = [];
      for (let i = from; i < to; i++) {
        this.years.push(new NavigationObject(i, i < this.minCalendarDate.year));
      }
    }
  }

  onDateSelect(date: Date) {
    if (this.isRange) {
      this.onRangeSelection(date);
    }
    else {
      this.selectedDate = date;
      this.onSelectionChange.emit(date)
    }
  }

  markDisabledDates() {
    this.markDisabled = (date: NgbDateStruct) => {
      if (this.selectedDate) {
        let selectedDate = new Date(this.selectedDate);
        let struct = {
          year: selectedDate.getFullYear(),
          month: selectedDate.getMonth() + 1,
          day: selectedDate.getDate()
        };
        if ((date as NgbDate).equals(struct)) {
          return false;
        }
      }
      if ((date as NgbDate).after(this.maxCalendarDate)) return true;
      return (date as NgbDate).before(this.minCalendarDate);
    };
  }

  navigateToPreviousMonth() {
    if (this.calendarInstance?.calendar) {
      const previous: NgbDate = this.calendarInstance.calendar.getPrev(this.navigationDate, 'm');
      if (previous.year > this.minCalendarDate.year || (previous.year == this.minCalendarDate.year && previous.month >= this.minCalendarDate.month)) {
        this.navigationDate = previous;
        this.calendarInstance?.navigateTo(this.navigationDate);
      }
    }
  }

  initYears() {
    let from: number = this.navigationDate.year - 7;
    let to: number = this.navigationDate.year + 7;
    this.years = [];
    for (let i = from; i <= to; i++) {
      this.years.push(new NavigationObject(i, i < this.minCalendarDate.year || i > this.maxCalendarDate.year));
    }
  }

  getNgbDate(date?: Date): NgbDate {
    date = new Date(date ?? this.currentDate);
    return new NgbDate(date.getFullYear(), date.getMonth() + 1, date.getDate());
  }


  initMonths() {
    this.months = [];
    for (let i = 1; i <= 12; i++) {
      this.months.push(new NavigationObject(i, false))
    }
  }


  get maxCalendarDate(): NgbDate {
    const date: Date = new Date(this.currentDate);
    return this.maxDate ?? this.getNgbDate(new Date(date.setFullYear(date.getFullYear() + 10)));
  }

  get minCalendarDate(): NgbDate {
    const date: Date = new Date(this.currentDate);
    return this.minDate ?? this.getNgbDate(new Date(date.setFullYear(date.getFullYear() - 100)));
  }


  yearSelected(event: Event, year: number) {
    event.preventDefault();
    event.stopPropagation()
    this.navigationDate.year = year
    this.calendarState = CalendarState.DAY
    this.calendarInstance?.navigateTo(this.navigationDate);
  }


  monthSelected(event: Event, month: number) {
    event.preventDefault();
    event.stopPropagation()
    this.navigationDate.month = month
    this.calendarState = CalendarState.DAY
    this.calendarInstance?.navigateTo(this.navigationDate);
  }




  onRangeSelection(date: Date): void {
    let selected = this.getNgbDate(date);
    if (this.maxDate) {
      if (selected.after(this.maxDate)) {
        selected = this.maxDate
      }
    }
    if (this.minDate) {
      if(selected.before(this.minDate)) {
        selected = this.minDate;
      }
    }
    if ((!this.fromDate && !this.toDate)) {
      this.fromDate = selected;
    } else if (this.fromDate && !this.toDate) {
      if (selected.before(this.fromDate)) {
        this.toDate = this.fromDate;
        this.fromDate = selected;
      } else {
        this.toDate = selected;
      }
    } else if (!this.fromDate && this.toDate) {
      if (selected.after(this.toDate)) {
        this.fromDate = this.toDate;
        this.toDate = selected;
      } else {
        this.fromDate = selected;
      }
    } else if (this.fromDate && this.toDate) {
      this.fromDate = selected;
      this.toDate = null;
    }
    this.onRangeChange.emit({from:this.fromDate, to:this.toDate});
  }


  isHovered(date: NgbDate): boolean {
    return (
        ((this.fromDate && !this.toDate) || (!this.fromDate && this.toDate)) &&
        ((this.hoveredDate && date.after(this.fromDate) && date.before(this.hoveredDate))
            ||
            (this.hoveredDate && date.before(this.fromDate) && date.after(this.hoveredDate))
            ||
            (this.hoveredDate && date.before(this.toDate) && date.after(this.hoveredDate))
            ||
            (this.hoveredDate && date.after(this.toDate) && date.before(this.hoveredDate)))
    );
  }


  isInside(date: NgbDate): boolean {
    return this.toDate && date.after(this.fromDate) && date.before(this.toDate);
  }

  isToday(date: NgbDate): boolean {
    return date.equals(this.ngbCalendar.getToday());
  }

  isDisabled(date: NgbDate): boolean {
    return !!(this.maxDate && date.after(this.maxDate)
        || (this.minDate && date.before(this.minDate)));
  }

  isInRange(date: NgbDate): boolean {
    return (
        date.equals(this.fromDate) ||
        (this.toDate && date.equals(this.toDate)) ||
        this.isInside(date) ||
        this.isHovered(date)
    );
  }



}

enum CalendarState {
  DAY,
  MONTH,
  YEAR
}

class NavigationObject {
  constructor(public value: number, public disabled: boolean) {
  }
}
